import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../features/auth/context/AuthContext';
import { PermissionScopes } from '../../features/auth/permissions';
import { useNotifications } from '../../features/notifications';
import { useToast } from '../../features/notifications';
import { useTranslation } from 'react-i18next';
import { setAppLanguage } from '../../i18n';
import { employeeService } from '../../features/admin/services/employeeService';
import { listCompanies } from '../../features/companies/services/companyService';
import { useChat } from '../../features/conversations/context/ChatContext';
import { createConversation, findExistingDirectConversation, toggleAiAssistant } from '../../features/conversations/services/conversationService';
import { isAiAssistantDirectConversation } from '../../features/conversations/hooks/useConversationPresentation';
import type { Employee } from '../../features/admin/types/Employee';
import { EmployeeAvatar } from './EmployeeAvatar';
import { Select } from './Select';
import { formatDateTime } from '../utils/localization';
import { getAppHostUrl } from '../config/workspaceHost';

const AI_ASSISTANT_PARTICIPANT_ID = '00000000-0000-0000-0000-000000000001';
const SHOW_ASSISTANT_LAUNCH_BUTTON = true;

export const Header: React.FC = () => {
  const { t, i18n } = useTranslation('header');
  const [open, setOpen] = React.useState(false);
  const [notificationsOpen, setNotificationsOpen] = React.useState(false);
  const [openingAssistant, setOpeningAssistant] = React.useState(false);
  const [isPending, startTransition] = React.useTransition();
  const [currentEmployee, setCurrentEmployee] = React.useState<Employee | null>(null);
  const [companyName, setCompanyName] = React.useState('');
  const notificationsRef = React.useRef<HTMLDivElement | null>(null);
  const profileMenuRef = React.useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const { setSelectedConversationId, setIsChatOpen, setIsChatMinimized } = useChat();
  const { logout, auth, hasPermission } = useAuth();
  const canViewAiUsage = hasPermission(PermissionScopes.aiTokenUsageRead);
  const appHostUrl = getAppHostUrl();
  const { showToast } = useToast();
  const {
    notifications,
    unreadCount,
    isLoading: isNotificationsLoading,
    markAsRead,
    markAllAsRead,
  } = useNotifications();

  React.useEffect(() => {
    let active = true;

    const loadCurrentEmployee = async () => {
      if (!auth?.tenantId) {
        setCurrentEmployee(null);
        setCompanyName('');
        return;
      }

      try {
        const [response, companies] = await Promise.all([
          employeeService.getCurrentEmployee(),
          listCompanies({ page: 1, pageSize: 1 }).catch(() => []),
        ]);

        if (active) {
          setCurrentEmployee(response?.employee ?? null);
          setCompanyName(companies[0]?.name?.trim() || '');
        }
      } catch {
        if (active) {
          setCurrentEmployee(null);
          setCompanyName('');
        }
      }
    };

    const handleProfileUpdated = (event: Event) => {
      const customEvent = event as CustomEvent<Employee>;
      setCurrentEmployee(customEvent.detail ?? null);
    };

    void loadCurrentEmployee();
    window.addEventListener('employee:profile-updated', handleProfileUpdated as EventListener);

    return () => {
      active = false;
      window.removeEventListener('employee:profile-updated', handleProfileUpdated as EventListener);
    };
  }, [auth?.tenantId]);

  React.useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!notificationsOpen) {
        return;
      }

      const target = event.target as Node | null;
      if (notificationsRef.current && target && !notificationsRef.current.contains(target)) {
        setNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [notificationsOpen]);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (profileMenuRef.current && target && !profileMenuRef.current.contains(target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  const onProfile = () => setOpen(prev => !prev);
  const onLogout = () => {
    setOpen(false);
    logout();
    navigate('/login');
  };

  const onOpenProfile = () => {
    setOpen(false);
    navigate('/profile');
  };

  const onOpenPlans = () => {
    setOpen(false);
    navigate('/plans');
  };

  const onOpenAiUsage = () => {
    setOpen(false);
    navigate('/ai-usage');
  };

  // Switching workspaces means leaving this hostname, so it goes through the global auth host.
  const onSwitchWorkspace = () => {
    setOpen(false);
    if (appHostUrl) {
      window.location.assign(`${appHostUrl}/workspaces`);
    }
  };

  const onLanguageChange = (nextLanguage: string) => {
    startTransition(() => {
      void setAppLanguage(nextLanguage);
    });
  };

  const onOpenAiAssistant = async () => {
    if (openingAssistant) {
      return;
    }

    setOpen(false);
    setNotificationsOpen(false);
    setOpeningAssistant(true);

    try {
      let conversation = await findExistingDirectConversation(AI_ASSISTANT_PARTICIPANT_ID);
      if (!conversation) {
        conversation = await createConversation(undefined, [{
          type: 'aiAssistant',
          id: AI_ASSISTANT_PARTICIPANT_ID,
        }]);
      }

      if (conversation.isAiAssistantMuted && isAiAssistantDirectConversation(conversation)) {
        conversation = await toggleAiAssistant(conversation.id, false) ?? conversation;
      }

      setSelectedConversationId(conversation.id);
      setIsChatMinimized(false);
      setIsChatOpen(true);
    } catch {
      showToast(t('failedToOpenAiAssistant'), 'error');
    } finally {
      setOpeningAssistant(false);
    }
  };

  const profileName = currentEmployee?.name || auth?.username || 'User';
  const profileSubtitleParts = [currentEmployee?.title, companyName || currentEmployee?.email].filter(Boolean);
  const profileSubtitle = profileSubtitleParts.join(' · ') || auth?.username || '';

  const onNotificationBellClick = () => {
    setNotificationsOpen(prev => !prev);
  };

  const onNotificationClick = async (notificationId: string, linkUrl: string | null) => {
    setNotificationsOpen(false);
    try {
      await markAsRead(notificationId);
    } catch {
      // keep UI responsive even if mark-as-read fails
    }

    if (linkUrl) {
      navigate(linkUrl);
    }
  };

  const onMarkAllRead = async () => {
    try {
      await markAllAsRead();
    } catch {
      // ignore API failures; unread badge will be corrected on refresh
    }
  };

  const formatNotificationTime = (timestamp: string) => formatDateTime(timestamp);

  return (
    <header className="app-header">
      <div className="header-left" />

      <div className="header-right">
        <div className="header-toolbar">
          <div className="header-toolbar-group">
            {SHOW_ASSISTANT_LAUNCH_BUTTON && (
              <button
                type="button"
                className="assistant-launch-btn"
                onClick={onOpenAiAssistant}
                disabled={openingAssistant}
                title={t('openAiAssistant')}
                aria-label={t('openAiAssistant')}
              >
                <span className="assistant-launch-btn-icon" aria-hidden="true">
                  <svg viewBox="0 0 16 16" fill="none" focusable="false">
                    <path
                      d="M8 1.5l1.2 3.1a2 2 0 001.2 1.2l3.1 1.2-3.1 1.2a2 2 0 00-1.2 1.2L8 12.5l-1.2-3.1a2 2 0 00-1.2-1.2L2.5 7l3.1-1.2a2 2 0 001.2-1.2L8 1.5z"
                      fill="currentColor"
                    />
                    <path d="M13 11.4l.45 1.15L14.6 13l-1.15.45L13 14.6l-.45-1.15L11.4 13l1.15-.45L13 11.4z" fill="currentColor" />
                  </svg>
                </span>
                <span className="assistant-launch-btn-label">{t('assistantLaunchLabel')}</span>
              </button>
            )}

            <div className="notifications-area" ref={notificationsRef}>
              <button
                className="header-icon-btn notification-bell-btn"
                aria-label={t('notifications')}
                aria-haspopup="true"
                aria-expanded={notificationsOpen}
                onClick={onNotificationBellClick}
              >
                <span className="notification-bell-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" focusable="false">
                    <path d="M9 18h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <path d="M6.5 16.5h11L16 14V10a4 4 0 1 0-8 0v4l-1.5 2.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                  </svg>
                </span>
                {unreadCount > 0 && (
                  <span className="notification-badge" aria-label={t('notificationsUnreadCount', { count: unreadCount })}>
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              {notificationsOpen && (
                <div className="notifications-menu" role="menu" aria-label={t('notifications')}>
                  <div className="notifications-menu-header">
                    <strong>{t('notifications')}</strong>
                    <button className="notifications-mark-all-btn" onClick={onMarkAllRead}>
                      {t('markAllRead')}
                    </button>
                  </div>

                  <div className="notifications-menu-list">
                    {isNotificationsLoading && notifications.length === 0 && (
                      <div className="notifications-empty">{t('notificationsLoading')}</div>
                    )}

                    {!isNotificationsLoading && notifications.length === 0 && (
                      <div className="notifications-empty">{t('notificationsEmpty')}</div>
                    )}

                    {notifications.map(notification => (
                      <button
                        key={notification.id}
                        className={`notifications-menu-item ${notification.isRead ? '' : 'unread'}`}
                        onClick={() => onNotificationClick(notification.id, notification.linkUrl)}
                      >
                        <div className="notifications-item-topline">
                          <span className="notifications-item-title">{notification.title || t('notifications')}</span>
                          {!notification.isRead && <span className="notifications-item-dot" aria-hidden="true" />}
                        </div>
                        <div className="notifications-item-body">{notification.body}</div>
                        <div className="notifications-item-time">{formatNotificationTime(notification.createdAtUtc)}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="header-toolbar-group header-toolbar-group-profile">
            <div className="profile-area" ref={profileMenuRef}>
              <button
                className="profile-btn"
                onClick={onProfile}
                aria-haspopup="true"
                aria-expanded={open}
                aria-label={profileName}
              >
                <EmployeeAvatar
                  name={profileName}
                  imageUrl={currentEmployee?.profileImageUrl}
                  colorPreference={currentEmployee?.colorPreference}
                  className="profile-avatar"
                  size={32}
                />
              </button>
              {open && (
                <div className="profile-menu" role="menu">
                  <div className="profile-menu-identity">
                    <EmployeeAvatar
                      name={profileName}
                      imageUrl={currentEmployee?.profileImageUrl}
                      colorPreference={currentEmployee?.colorPreference}
                      className="profile-menu-avatar"
                      size={40}
                    />
                    <div className="profile-menu-copy">
                      <strong>{profileName}</strong>
                      <span>{profileSubtitle || t('tenant')}</span>
                    </div>
                  </div>
                  <div className="profile-menu-language">
                    <span>{t('languageLabel')}</span>
                    <Select
                      variant="compact"
                      menuClassName="profile-menu-language-menu"
                      value={i18n.resolvedLanguage || i18n.language || 'az'}
                      onChange={onLanguageChange}
                      disabled={isPending}
                      ariaLabel={t('languageLabel')}
                      options={[
                        { value: 'az', label: t('languageAz') },
                        { value: 'en', label: t('languageEn') },
                        { value: 'tr', label: t('languageTr') },
                      ]}
                    />
                  </div>
                  <button className="profile-menu-item" onClick={onOpenProfile}>{t('myProfile')}</button>
                  <button className="profile-menu-item" onClick={onOpenPlans}>{t('plans')}</button>
                  {appHostUrl ? (
                    <button className="profile-menu-item" onClick={onSwitchWorkspace}>{t('switchWorkspace')}</button>
                  ) : null}
                  {canViewAiUsage ? (
                    <button className="profile-menu-item" onClick={onOpenAiUsage}>{t('aiUsage')}</button>
                  ) : null}
                  <button className="profile-menu-item profile-menu-item-danger" onClick={onLogout}>{t('logout')}</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;